import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { ArrowLeft, CheckCircle, FileCheck, Pencil, Play, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router'

import { isApproverOrHigherRole } from '@ankor-io/common/domain/iam/account'
import {
  type CharterStatsPayload,
  CharterStatus,
  type CharterSummary as CharterSummaryType,
  type UpdateCharterRequest,
} from '@ankor-io/common/types/charter'
import type { TransactionPayload } from '@ankor-io/common/types/transaction/transaction'
import { getStatusColour, getStatusText } from '@ankor-io/common/util/enum-to-string/charter'

import { useCharter } from '@/hooks/useCharter'
import { useUserContext } from '@/providers/user/useUserContext'
import { Badge } from '@/ui-components/Badge'
import { Button } from '@/ui-components/Button'
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@/ui-components/Dropdown'
import { Heading } from '@/ui-components/Heading'
import Loading from '@/ui-components/Loading'
import Tabs, { TabContent, TabItem } from '@/ui-components/Tabs'
import { getTransactionQueryParams } from '@/utils/getQueryParams/getTransactionQueryParams'
import CharterSummary from '@/views/CharterDetail/CharterSummary'
import FilesAndDocuments from '@/views/CharterDetail/FilesAndDocuments'
import Transactions from '@/views/CharterDetail/Transactions'
import ConfirmationModal from '@/views/components/ConfirmationModal'
import type { TransactionFilters } from '@/views/components/TransactionFilters'

const TRANSACTIONS_PER_PAGE = 100
const SKIP = 0

const CharterDetailPage: React.FC = () => {
  const navigate = useNavigate()
  const { charterId } = useParams()
  const { getAccessToken } = useKindeAuth()
  const { charter, setCharter } = useCharter()
  const { activeOrganizationId, activeConnectedAccountId, activeOrganizationRole } = useUserContext()

  const [isLoading, setIsLoading] = useState(true)
  const [isStartingCharter, setIsStartingCharter] = useState(false)

  const [stats, setStats] = useState<CharterStatsPayload | null>(null)
  const [transactions, setTransactions] = useState<TransactionPayload[]>([])
  const [transactionFilters, setTransactionFilters] = useState<TransactionFilters>({
    dateFrom: '',
    dateTo: '',
    status: '',
    categoryId: '',
    type: '',
  })

  const [isCompletingCharter, setIsCompletingCharter] = useState(false)
  const [isCancellingCharter, setIsCancellingCharter] = useState(false)
  const [isCancelCharterConfirmationModalOpen, setIsCancelCharterConfirmationModalOpen] = useState(false)

  const [activeTab, setActiveTab] = useState<'expenses' | 'files-and-documents'>('expenses')

  useEffect(() => {
    if (!charterId || !activeOrganizationId || !activeConnectedAccountId) {
      return
    }

    const queryParams = getTransactionQueryParams(TRANSACTIONS_PER_PAGE, SKIP, transactionFilters)

    // If we already have the charter data for this ID, only refresh transactions
    if (charterId === charter?.id) {
      const fetchTransactionsAndStatsOnly = async () => {
        try {
          const accessToken = await getAccessToken()
          const baseUrl = `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charterId}`

          const [transactionsResponse, statsResponse] = await Promise.all([
            fetch(`${baseUrl}/transactions?${queryParams.toString()}`, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }),
            fetch(`${baseUrl}/stats`, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }),
          ])

          if (!transactionsResponse.ok) {
            throw new Error(`Failed to fetch transactions: ${transactionsResponse.status}`)
          }

          if (!statsResponse.ok) {
            throw new Error(`Failed to fetch stats: ${statsResponse.status}`)
          }

          const [transactionsData, statsData] = await Promise.all([
            transactionsResponse.json() as Promise<TransactionPayload[]>,
            statsResponse.json() as Promise<CharterStatsPayload>,
          ])

          setTransactions(transactionsData)
          setStats(statsData)
        } catch (error) {
          console.error('Error fetching charter data:', error)
        } finally {
          setIsLoading(false)
        }
      }

      fetchTransactionsAndStatsOnly()
    }

    const fetchData = async () => {
      try {
        const accessToken = await getAccessToken()
        const baseUrl = `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charterId}`

        // Run both requests in parallel
        const [charterResponse, transactionsResponse, statsResponse] = await Promise.all([
          fetch(baseUrl, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }),
          fetch(`${baseUrl}/transactions?${queryParams.toString()}`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }),
          fetch(`${baseUrl}/stats`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }),
        ])

        // Check both responses
        if (!charterResponse.ok) {
          throw new Error(`Failed to fetch charter: ${charterResponse.status}`)
        }

        if (!transactionsResponse.ok) {
          throw new Error(`Failed to fetch transactions: ${transactionsResponse.status}`)
        }

        if (!statsResponse.ok) {
          throw new Error(`Failed to fetch stats: ${statsResponse.status}`)
        }

        // Parse both responses in parallel
        const [charterData, transactionsData, statsData] = await Promise.all([
          charterResponse.json() as Promise<CharterSummaryType>,
          transactionsResponse.json() as Promise<TransactionPayload[]>,
          statsResponse.json() as Promise<CharterStatsPayload>,
        ])

        // Update state with both results
        setCharter(charterData)
        setTransactions(transactionsData)
        setStats(statsData)
      } catch (error) {
        console.error('Error fetching charter data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charterId, activeOrganizationId, activeConnectedAccountId, getAccessToken])

  useEffect(() => {
    if (isLoading) {
      return
    }

    const queryParams = getTransactionQueryParams(TRANSACTIONS_PER_PAGE, SKIP, transactionFilters)
    const fetchTransactions = async () => {
      const response = await fetch(
        `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charterId}/transactions?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${await getAccessToken()}`,
          },
        },
      )

      if (!response.ok) {
        throw new Error(`Failed to fetch transactions: ${response.status}`)
      }

      const data: TransactionPayload[] = await response.json()
      setTransactions(data)
    }

    fetchTransactions()
    // We only want to re-fetch transactions when the transaction filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionFilters])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  const handleCharterEdited = (charter: CharterSummaryType) => {
    setCharter(charter)
  }

  const handleStartCharter = () => {
    const startCharter = async () => {
      if (!charter) {
        throw new Error('Charter not found')
      }

      // Currently with the dropdown field it closes upon clicking a button inside of it, so we won't really see this disabled but if the users internet is slow and go back into the menu, this can prevent it
      setIsStartingCharter(true)

      try {
        // convert embarkationDate and disembarkationDate to ISO-8601 DateTime
        const payload: UpdateCharterRequest = {
          ...charter,
          status: CharterStatus.IN_PROGRESS,
        }
        delete payload.currencyCode

        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charter.id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            method: 'PUT',
            body: JSON.stringify(payload),
          },
        )

        if (!res.ok) {
          throw new Error('Failed to start charter.')
        }

        const data: CharterSummaryType = await res.json()
        handleCharterEdited(data)
      } catch (error) {
        console.error('Error starting charter:', error)
        throw new Error('Failed to start charter.')
      } finally {
        setIsStartingCharter(false)
      }
    }

    toast.promise(startCharter, {
      loading: 'Starting charter...',
      success: 'Charter started successfully.',
      error: 'Failed to start charter.',
    })
  }

  const handleCancelCharter = () => {
    const cancelCharter = async () => {
      if (!charter) {
        throw new Error('Charter not found')
      }

      // Currently with the dropdown field it closes upon clicking a button inside of it, so we won't really see this disabled but if the users internet is slow and go back into the menu, this can prevent it
      setIsCancellingCharter(true)

      try {
        // Convert embarkationDate and disembarkationDate to ISO-8601 DateTime
        const payload: UpdateCharterRequest = {
          ...charter,
          status: CharterStatus.CANCELLED,
        }
        delete payload.currencyCode

        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charter.id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            method: 'PUT',
            body: JSON.stringify(payload),
          },
        )

        if (!res.ok) {
          throw new Error('Failed to cancel charter.')
        }

        const data: CharterSummaryType = await res.json()
        handleCharterEdited(data)
      } catch (error) {
        console.error('Error cancelling charter:', error)
        throw new Error('Failed to cancel charter.')
      } finally {
        setIsCancelCharterConfirmationModalOpen(false)
        setIsCancellingCharter(false)
      }
    }

    toast.promise(cancelCharter, {
      loading: 'Cancelling charter...',
      success: 'Charter cancelled successfully.',
      error: 'Failed to cancel charter.',
    })
  }

  const handleCompleteCharter = () => {
    const completeCharter = async () => {
      if (!charter) {
        throw new Error('Charter not found')
      }

      // Currently with the dropdown field it closes upon clicking a button inside of it, so we won't really see this disabled but if the users internet is slow and go back into the menu, this can prevent it
      setIsCompletingCharter(true)

      try {
        // Convert embarkationDate and disembarkationDate to ISO-8601 DateTime
        const payload: UpdateCharterRequest = {
          ...charter,
          status: CharterStatus.COMPLETED,
        }
        delete payload.currencyCode

        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charter.id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            method: 'PUT',
            body: JSON.stringify(payload),
          },
        )

        if (!res.ok) {
          throw new Error('Failed to complete charter.')
        }

        const data: CharterSummaryType = await res.json()
        handleCharterEdited(data)
      } catch (error) {
        console.error('Error completing charter:', error)
        throw new Error('Failed to complete charter.')
      } finally {
        setIsCompletingCharter(false)
      }
    }

    toast.promise(completeCharter, {
      loading: 'Completing charter...',
      success: 'Charter completed successfully.',
      error: 'Failed to complete charter.',
    })
  }

  if (isLoading) {
    return <Loading />
  }

  if (!charter) {
    // can we have a better empty state here?
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-8rem)] gap-2">
        <Heading level={3}>Charter not found</Heading>
        <Button
          color="primary"
          onClick={() => navigate(-1)}
        >
          Go back
        </Button>
      </div>
    )
  }

  return (
    <>
      <Button
        plain
        className="mb-2"
        to="/charters"
        aria-label="Go back"
      >
        <ArrowLeft data-slot="icon" />
        Back
      </Button>

      <div className="space-y-6">
        <div className="flex justify-between md:items-center flex-col md:flex-row gap-2">
          <div className="flex flex-col">
            <div className="flex flex-col sm:flex-row gap-x-2">
              <Heading level={3}>Charter: {charter?.name}</Heading>
              <Badge
                className="w-fit sm:w-auto"
                color={getStatusColour(charter.status)}
              >
                {getStatusText(charter.status)}
              </Badge>
            </div>
            <p className="text-sm text-gray-500">
              {formatDate(charter.startDate)} - {formatDate(charter.endDate)}
            </p>
          </div>

          {charter.status !== CharterStatus.COMPLETED && charter.status !== CharterStatus.CANCELLED && (
            <Dropdown>
              <DropdownButton color="dark">Actions</DropdownButton>
              {/* All buttons disabled if just 1 of these is clicked as they should not be able to run in parallel as the state is affected */}
              <DropdownMenu anchor="bottom end">
                {/* Start Charter */}
                {charter.status === CharterStatus.UPCOMING && (
                  <DropdownItem
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isStartingCharter || isCancellingCharter}
                    onClick={() => handleStartCharter()}
                    data-charter-status={charter.status}
                    data-action-type="start-charter"
                  >
                    <Play data-slot="icon" />
                    {isStartingCharter ? 'Starting...' : 'Start'}
                  </DropdownItem>
                )}

                {/* Edit Charter */}
                {(charter.status !== CharterStatus.SIGNING_OFF || isApproverOrHigherRole(activeOrganizationRole)) && (
                  <DropdownItem
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isStartingCharter || isCancellingCharter}
                    to={`/charters/${charter.id}/edit`}
                    data-charter-status={charter.status}
                    data-action-type="edit-charter"
                  >
                    <Pencil data-slot="icon" />
                    Edit
                  </DropdownItem>
                )}

                {/* Sign-off Charter */}
                {charter.status === CharterStatus.IN_PROGRESS && isApproverOrHigherRole(activeOrganizationRole) && (
                  <DropdownItem
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                    to={`/charters/${charter.id}/sign-off`}
                    data-charter-status={charter.status}
                    data-action-type="sign-off-charter"
                    data-testid="sign-off-charter"
                  >
                    <FileCheck data-slot="icon" />
                    Sign-off
                  </DropdownItem>
                )}

                {/* Cancel Charter */}
                {isApproverOrHigherRole(activeOrganizationRole) && (
                  <DropdownItem
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isStartingCharter || isCancellingCharter}
                    onClick={() => setIsCancelCharterConfirmationModalOpen(true)}
                    data-charter-status={charter.status}
                    data-action-type="cancel-charter"
                  >
                    <X data-slot="icon" />
                    Cancel
                  </DropdownItem>
                )}

                {/* Complete Charter */}
                {isApproverOrHigherRole(activeOrganizationRole) && charter.status === CharterStatus.SIGNING_OFF && (
                  <DropdownItem
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isCompletingCharter}
                    onClick={() => handleCompleteCharter()}
                    data-charter-status={charter.status}
                    data-action-type="complete-charter"
                  >
                    <CheckCircle data-slot="icon" />
                    Complete
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
          )}
        </div>

        <CharterSummary
          charter={charter}
          stats={stats!}
        />

        <Tabs>
          <TabItem
            current={activeTab === 'expenses'}
            onClick={() => setActiveTab('expenses')}
          >
            Expenses
          </TabItem>
          <TabItem
            current={activeTab === 'files-and-documents'}
            onClick={() => setActiveTab('files-and-documents')}
          >
            Files and Documents
          </TabItem>
        </Tabs>

        <TabContent isActive={activeTab === 'expenses'}>
          <Transactions
            charter={charter}
            transactions={transactions}
            transactionFilters={transactionFilters}
            setTransactionFilters={setTransactionFilters}
            onUpdatedTransactions={setTransactions}
          />
        </TabContent>

        <TabContent isActive={activeTab === 'files-and-documents'}>
          <FilesAndDocuments charterId={charter.id} />
        </TabContent>
      </div>

      <ConfirmationModal
        isOpen={isCancelCharterConfirmationModalOpen}
        onClose={() => setIsCancelCharterConfirmationModalOpen(false)}
        title="Cancel Charter"
        description="Are you sure you want to cancel this charter? You won't be able to attribute any expense to this charter anymore."
        confirmText="Cancel Charter"
        confirmLoadingText="Cancelling charter..."
        confirmColour="red"
        isLoading={isCancellingCharter}
        onConfirm={handleCancelCharter}
      />
    </>
  )
}

export default CharterDetailPage
